"use client"

import { useId, useState, type ReactNode } from "react"

interface TooltipProps {
  /** Elemento que dispara o tooltip (hover/focus). */
  children: ReactNode
  /** Conteúdo exibido. Se vazio/null, o tooltip não aparece. */
  content: ReactNode
  /** Largura máxima do balão (px). */
  maxWidth?: number
  className?: string
}

/**
 * Tooltip acessível leve (sem dependência nova). Aparece no hover e no foco
 * por teclado; some no blur/leave/Escape. Posicionado acima do gatilho.
 */
export function Tooltip({
  children,
  content,
  maxWidth = 260,
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false)
  const id = useId()

  if (!content) return <>{children}</>

  return (
    <span
      className={`relative inline-flex ${className ?? ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(false)
      }}
    >
      <span className="min-w-0" aria-describedby={open ? id : undefined}>
        {children}
      </span>
      {open && (
        <span
          role="tooltip"
          id={id}
          className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 animate-in rounded-lg px-3 py-2 text-left text-[12px] leading-snug shadow-lg duration-100 zoom-in-95 fade-in"
          style={{
            maxWidth,
            width: "max-content",
            background: "var(--ink-strong, #1c1917)",
            color: "#fff",
          }}
        >
          {content}
          <span
            aria-hidden
            className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent"
            style={{ borderTopColor: "var(--ink-strong, #1c1917)" }}
          />
        </span>
      )}
    </span>
  )
}
